import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import { srOnly } from '../organic/motion';

/*
 * The account requests queue (routes/account_requests.py, migration 0106):
 * who registered or asked for more access, and as what -- a public guest, an
 * external researcher, a researcher, lab staff. It replaced the pending public
 * registrations list (2026-10-29); a request appears once its address is
 * verified, and every manager is emailed then.
 *
 * Approving grants the roles ticked in its dialog -- the requested type's by
 * default, an upgrade keeping the account's own but `public` -- under the
 * /users form's rules (only a system admin makes a system admin), and
 * activates the account. Rejecting needs a reason, which the person is sent.
 * It lives on /users: whoever reads accounts sees it (users:read), whoever
 * manages them decides (users:manage; `readOnly` otherwise).
 *
 * `roles` are the roles this manager may offer (pages/Users.js), and
 * `onChanged` lets the page read its own account list again after a change.
 */
const STATUSES = ['pending', 'approved', 'rejected', 'withdrawn', 'all'];

/** The roles approving starts from: what the request asks for, an upgrade
 *  keeping what the account holds but `public`. */
export function startingRoles(request) {
  const asked = request.default_roles || [];
  if (request.kind !== 'upgrade') return [...asked];
  const kept = (request.roles || []).filter(r => r !== 'public');
  return [...kept, ...asked.filter(r => !kept.includes(r))];
}

export default function AccountRequests({ onChanged, readOnly = false, roles = [] }) {
  const { t } = useTranslation(['users', 'common']);
  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState('');
  const [approving, setApproving] = useState(null);   // { request, roles }
  const [rejecting, setRejecting] = useState(null);   // { request, reason }
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const load = useCallback(() => api.get('/account-requests', { params: { status } })
    .then(res => { setLoadFailed(false); setRequests(res.data || []); })
    .catch(() => setLoadFailed(true)), [status]);
  useEffect(() => { load(); }, [load]);

  const nameOf = (r) => r.full_name || r.username || r.email;

  const decided = async (message) => {
    setApproving(null);
    setRejecting(null);
    setNotice(message);
    await load();
    onChanged?.();
  };

  const approve = async () => {
    setBusy(true);
    setDialogError('');
    try {
      const { request } = approving;
      const res = await api.post(`/account-requests/${request.request_id}/approve`, { roles: approving.roles });
      await decided(t(res.data?.mail_sent === false ? 'requests.approvedNoMail' : 'requests.approved',
        { name: nameOf(request) }));
    } catch (err) {
      setDialogError(err.response?.data?.error || t('requests.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setDialogError('');
    try {
      const { request, reason } = rejecting;
      const res = await api.post(`/account-requests/${request.request_id}/reject`, { reason: reason.trim() });
      await decided(t(res.data?.mail_sent === false ? 'requests.rejectedNoMail' : 'requests.rejected',
        { name: nameOf(request) }));
    } catch (err) {
      setDialogError(err.response?.data?.error || t('requests.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const toggleRole = (role) => setApproving(a => ({
    ...a, roles: a.roles.includes(role) ? a.roles.filter(r => r !== role) : [...a.roles, role],
  }));
  // A request may ask for a role this manager does not otherwise offer (the
  // executive, to a collection manager): it is shown so it can be unticked.
  const offered = approving ? [...roles, ...approving.roles.filter(r => !roles.includes(r))] : roles;
  const pendingView = status === 'pending';

  return (
    <section style={box} aria-labelledby="account-requests-title" data-testid="account-requests">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <h3 id="account-requests-title" style={{ margin: 0, fontSize: 16 }}>
          {t('requests.title')}{pendingView && requests?.length ? ` (${requests.length})` : ''}
        </h3>
        <label style={{ ...muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {t('requests.show')}
          <select value={status} onChange={e => { setNotice(''); setStatus(e.target.value); }}
            title={t('tip.requestStatus')} data-testid="requests-status">
            {STATUSES.map(s => <option key={s} value={s}>{t(`requests.status.${s}`)}</option>)}
          </select>
        </label>
      </div>
      <p style={{ ...muted, margin: '0 0 8px' }}>{t('requests.intro')}</p>
      {loadFailed && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: 0 }}>{t('requests.loadFailed')}</p>}
      {!loadFailed && requests === null && <p style={{ ...muted, margin: 0 }}>{t('common:loading')}</p>}
      {requests?.length === 0 && <p style={{ ...muted, margin: 0 }}>{t('requests.none')}</p>}
      {notice && <p role="status" style={{ color: 'var(--c-success-fg)', margin: '0 0 8px' }}>{notice}</p>}
      {requests?.length > 0 && (
        <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>{t('requests.name')}</th>
                <th style={th}>{t('requests.email')}</th>
                <th style={th}>{t('requests.asksFor')}</th>
                <th style={th}>{t('requests.about')}</th>
                <th style={th}>{t('requests.when')}</th>
                {!pendingView && <th style={th}>{t('requests.decision')}</th>}
                <th style={th}><span style={srOnly}>{t('requests.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => {
                const when = r.created_at ? new Date(r.created_at) : null;
                return (
                  <tr key={r.request_id} style={{ borderTop: '1px solid var(--c-border)' }}
                    data-testid={`request-${r.request_id}`}>
                    <td style={td}>{r.full_name || r.username || '—'}</td>
                    <td style={{ ...td, color: 'var(--c-text-muted-fg)' }} dir="ltr">{r.email}</td>
                    <td style={td}>
                      {t(`requests.type.${r.requested_type}`)}
                      {r.kind === 'upgrade' && <span style={badge}>{t('requests.upgrade')}</span>}
                    </td>
                    <td style={td}>
                      {r.affiliation || '—'}
                      {r.purpose && <div style={muted}>{r.purpose}</div>}
                    </td>
                    <td style={td}>{when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString() : '—'}</td>
                    {!pendingView && (
                      <td style={td} data-testid={`decision-${r.request_id}`}>
                        {t(`requests.status.${r.status}`)}
                        {r.decided_by && <div style={muted}>{r.decided_by}</div>}
                        {r.decision_reason && <div style={muted}>{r.decision_reason}</div>}
                      </td>
                    )}
                    <td style={{ ...td, textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {/* A reader (users:read without users:manage) sees who waits. */}
                      {!readOnly && r.status === 'pending' && (<>
                        <Button variant="primary" onClick={() => {
                          setDialogError(''); setApproving({ request: r, roles: startingRoles(r) });
                        }} title={t('tip.approveRequest')} data-testid={`approve-${r.request_id}`}>
                          {t('requests.approve')}
                        </Button>
                        <Button variant="danger" onClick={() => { setDialogError(''); setRejecting({ request: r, reason: '' }); }}
                          title={t('tip.rejectRequest')} style={{ marginInlineStart: 8 }}
                          data-testid={`reject-${r.request_id}`}>
                          {t('requests.reject')}
                        </Button>
                      </>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!approving} title={approving ? t('requests.approveTitle', { name: nameOf(approving.request) }) : ''}
        onClose={() => setApproving(null)} busy={busy} testId="approve-dialog">
        {approving && (<>
          <p style={{ margin: '0 0 8px' }}>
            {t('requests.approveIntro', { type: t(`requests.type.${approving.request.requested_type}`) })}
          </p>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 600, marginBottom: 6 }}>{t('requests.rolesLabel')}</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {offered.map(role => (
                <label key={role} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={approving.roles.includes(role)} onChange={() => toggleRole(role)}
                    data-testid={`grant-${role}`} />
                  {t(`role.${role}`)}
                </label>
              ))}
            </div>
          </fieldset>
          {approving.roles.length === 0 && <p style={{ ...muted, margin: '8px 0 0' }}>{t('requests.rolesRequired')}</p>}
          {approving.request.kind === 'upgrade' && (
            <p style={{ ...muted, margin: '8px 0 0' }}>{t('requests.upgradeSignOut')}</p>
          )}
          {dialogError && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: '8px 0 0' }}>{dialogError}</p>}
          <ActionBar>
            <Button onClick={() => setApproving(null)} disabled={busy}>{t('common:cancel')}</Button>
            <Button variant="primary" onClick={approve} busy={busy} disabled={approving.roles.length === 0}
              data-testid="approve-confirm">
              {busy ? t('requests.approving') : t('requests.approve')}
            </Button>
          </ActionBar>
        </>)}
      </Dialog>

      <Dialog open={!!rejecting} title={rejecting ? t('requests.rejectTitle', { name: nameOf(rejecting.request) }) : ''}
        onClose={() => setRejecting(null)} busy={busy} dirty={!!rejecting?.reason.trim()} testId="reject-dialog">
        {rejecting && (<>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>{t('requests.reasonLabel')}</span>
            <textarea value={rejecting.reason} rows={3} maxLength={1000} style={{ width: '100%', boxSizing: 'border-box' }}
              onChange={e => setRejecting(r => ({ ...r, reason: e.target.value }))} data-testid="reject-reason" />
          </label>
          <p style={{ ...muted, margin: '4px 0 0' }}>{t('requests.reasonHelp')}</p>
          {dialogError && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: '8px 0 0' }}>{dialogError}</p>}
          <ActionBar>
            <Button onClick={() => setRejecting(null)} disabled={busy}>{t('common:cancel')}</Button>
            <Button variant="danger" onClick={reject} busy={busy} disabled={rejecting.reason.trim().length < 3}
              data-testid="reject-confirm">
              {busy ? t('requests.rejecting') : t('requests.reject')}
            </Button>
          </ActionBar>
        </>)}
      </Dialog>
    </section>
  );
}

const box = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 16,
  marginBottom: 20 };
const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)' };
const th = { padding: '6px 8px', textAlign: 'start', fontWeight: 600, fontSize: 13 };
const td = { padding: '6px 8px', overflowWrap: 'anywhere' };
const badge = { marginInlineStart: 6, fontSize: 12, padding: '1px 6px', borderRadius: 10,
  background: 'var(--c-bg-muted, var(--c-border))' };
